import Badge from 'components/badge/badge.module';
import LaunchCards from 'components/launchCards/launchCards.module';
import { PoweredBy } from 'components/poweredBy/poweredBy.module';
import { ScrollToBottom } from 'components/scrollToBottom/scrollToBottom.module';
import { ENGT_ID, REGEX_EQUATION_TO_CHECK_FOR_HTML_TAG } from 'consts/consts';
import { BADGE_TYPE, CAROUSEL_TYPE, SCREEN_TYPE } from 'consts/enum';
import { LS } from 'consts/localStorage';
import {
	addClass,
	engtLSEventListener,
	getUserWebAttributeObject,
	readFromLs,
	removeClass,
	setCustomTagHTML,
	stopCurrentFlowandSendMsg,
} from 'helpers/helpers';
import { updateAttribute } from 'helpers/socket';
import { ENGT_CHAT } from 'js/bot.model';
import { ERROR_MESSAGE } from 'components/replyInputArea/replyInput.model';
import { launchScreen } from './launchScreen.model';
import { LaunchScreenTemplate } from './launchScreen.template';

const LAUNCH_INPUT_ERROR_TIMEOUT = 3000;

export default class LaunchScreen {
	constructor(props) {
		this.initData(props);
		this.initWrapper();
		this.initComponents();
		this.initEventHandlers();
	}

	initData(props) {
		this.data = launchScreen;
		this.setter({
			...props,
			badgeId: ENGT_ID.UNREAD_COUNT_BADGE,
			count: ENGT_CHAT.get('unReadMessages')?.length || 0,
			queryTitle: ENGT_CHAT.get('config.queryTitle'),
			queryPlaceholder: ENGT_CHAT.get('config.queryPlaceholder'),
			themeColor: ENGT_CHAT.get('config.themeColor'),
			launchCards: ENGT_CHAT.get('config.launchCards'),
		});
	}

	initWrapper() {
		const wrapper = document.createElement('div');

		wrapper.classList.add('engt-launch-screen');
		wrapper.id = 'engtLaunchScreen';
		wrapper.innerHTML = LaunchScreenTemplate(this.data);

		this.html = wrapper;
	}

	initComponents() {
		const { badgeId, count } = this.data;
		const poweredBy = new PoweredBy().render();

		const badge = new Badge({
			id: badgeId,
			type: BADGE_TYPE.MINI,
			count,
		}).render();
		const scrollDownBtn = new ScrollToBottom({
			id: 'engtLaunchScreenDetails',
		}).render();

		setCustomTagHTML(this.html.querySelector('scroll-to-bottom'), scrollDownBtn);
		setCustomTagHTML(this.html.querySelector('unread-badge'), badge);
		setCustomTagHTML(this.html.querySelector('powered-by'), poweredBy);
	}

	initEventHandlers() {
		const ref = this;
		const regex = REGEX_EQUATION_TO_CHECK_FOR_HTML_TAG;

		const displayLaunchInputError = (message) => {
			const errorDiv = ref.html.querySelector('#engtLaunchInputError');
			if (errorDiv) {
				errorDiv.textContent = message;
				errorDiv.style.display = 'block';
				setTimeout(() => {
					errorDiv.style.display = 'none';
				}, LAUNCH_INPUT_ERROR_TIMEOUT);
			}
		};

		const trySendQuery = (value) => {
			if (regex.test(value)) {
				displayLaunchInputError(
					ENGT_CHAT.get('GENERIC_STRINGS')?.['HTML_TAG_NOT_ALLOWED'] || ERROR_MESSAGE.htmlTagNotAllowed
				);
				return;
			}
			ref.sendQuery(value);
		};

		this.html.querySelector('.engt-launch-query-send-btn').addEventListener('click', () => {
			trySendQuery(ref.html.querySelector('#engtQueryInput').value);
		});
		this.html.querySelector('#engtQueryInput').addEventListener('keydown', (event) => {
			if (event.key === 'Enter') {
				trySendQuery(event.target.value);
			}
		});

		ENGT_CHAT.set('sendQuery', this.sendQuery);
		ENGT_CHAT.set('renderUnreadCount', this.renderUnreadCount);
		ENGT_CHAT.set('reRenderLaunchScreen', this.reRenderLaunchScreen);
		ENGT_CHAT.set('updatePreviewMessage', this.updatePreviewMessage);
	}

	sendQuery = (message, config = {}) => {
		if (ENGT_CHAT.get('startConvoAdded')) {
			if (readFromLs(LS().GOT_UID) === 'false') {
				engtLSEventListener(LS().GOT_UID, 'itemInserted', () => {
					stopCurrentFlowandSendMsg(message, config);
				});
			}
			ENGT_CHAT.get('startNewConversation')(message, config);
		} else {
			if (ENGT_CHAT.get('isFirstMessage')) {
				const webUrl = window.location.href;
				const webPage = document.title;
				const attributes = getUserWebAttributeObject(webUrl, webPage);
				updateAttribute([attributes]);
			}
			ENGT_CHAT.get('sendMessage')(message, config);
		}
		this.hideLaunchScreen();
	};

	hideLaunchScreen = () => {
		document.getElementById('engtQueryInput').value = '';
		if (ENGT_CHAT.get('getCurrentScreen')() === SCREEN_TYPE.LAUNCH) {
			ENGT_CHAT.get('showChatScreen')();
		}
	};

	checkClassAndUpdateClass = (notificationBadge, classToBeChecked, classToBeAdded) => {
		if (notificationBadge.classList?.contains(classToBeChecked)) {
			removeClass(notificationBadge, classToBeChecked);
			addClass(notificationBadge, classToBeAdded);
		}
	};

	renderUnreadCount = (count) => {
		const unreadNotificationBadge = document.getElementById('unreadNotificationBadge');
		const unreadLaunchscreenNotificationBadge = document.getElementById('unreadCountLaunchScreen');
		if (count > 9 && count < 100) {
			if (unreadNotificationBadge.classList.contains('engt-badge')) {
				this.checkClassAndUpdateClass(unreadNotificationBadge, 'engt-badge-small', 'engt-badge-medium');
			}
			if (unreadLaunchscreenNotificationBadge.classList.contains('engt-badge')) {
				this.checkClassAndUpdateClass(
					unreadLaunchscreenNotificationBadge,
					'engt-badge-mini',
					'engt-badge-xSmall'
				);
			}
		} else if (count > 99) {
			if (unreadNotificationBadge.classList.contains('engt-badge')) {
				this.checkClassAndUpdateClass(unreadNotificationBadge, 'engt-badge-medium', 'engt-badge-large');
				this.checkClassAndUpdateClass(unreadNotificationBadge, 'engt-badge-small', 'engt-badge-large');
			}
			if (unreadLaunchscreenNotificationBadge.classList.contains('engt-badge')) {
				this.checkClassAndUpdateClass(
					unreadLaunchscreenNotificationBadge,
					'engt-badge-xSmall',
					'engt-badge-custom'
				);
				this.checkClassAndUpdateClass(
					unreadLaunchscreenNotificationBadge,
					'engt-badge-mini',
					'engt-badge-custom'
				);
			}
		}
		ENGT_CHAT.get('setBadgeCount')(ENGT_ID.UNREAD_COUNT_BADGE, count);
		ENGT_CHAT.get('setBadgeCount')(ENGT_ID.UNREAD_NOTIFICATION_BADGE, count);
	};

	updatePreviewMessage = () => {
		const previewContainer = document.getElementById('launchPreview');
		const previewTextArea = document.getElementById('launchPreviewMessage');
		const previewTimestamp = document.getElementById('launchPreviewTime');
		const messages = document.querySelectorAll('.engt-msg-container');
		const lastMessage = messages[messages.length - 1];

		if (previewTextArea && lastMessage?.dataset?.message) {
			const div = document.createElement('div');
			div.innerHTML = lastMessage.getAttribute('data-message');
			removeClass(previewContainer, 'engt-launch-preview-hidden');
			previewTextArea.innerText = div.innerText;
			previewTimestamp.innerText = lastMessage.getAttribute('data-msg-clock-time');
		}
	};

	renderLaunchCards = (reRender = false) => {
		const { launchCards: cards } = this.data;
		const launchCardElem = reRender && document.getElementById('engtLaunchCards');
		if (launchCardElem) {
			launchCardElem.outerHTML = '<launch-cards></launch-cards>';
		}

		const launchCardTag = this.html.querySelector('launch-cards');

		if (launchCardTag) {
			const launchCardType =
				cards?.length === 1 ? CAROUSEL_TYPE.VERTICAL : ENGT_CHAT.get('config.launchCardType');
			const launchCards = cards?.length
				? new LaunchCards({
						type: launchCardType,
				  }).render()
				: '';
			launchCards && setCustomTagHTML(launchCardTag, launchCards);
		}
	};

	reRenderLaunchScreen = (updateData = false) => {
		updateData && this.initData({ launchCards: ENGT_CHAT.get('config.launchCards') });
		this.renderLaunchCards(updateData);
		this.renderUnreadCount(ENGT_CHAT.get('unReadMessages')?.length);
		this.updatePreviewMessage();
	};

	setter(propData) {
		this.data = {
			...this.data,
			...propData,
		};
	}

	render() {
		return this.html;
	}
}
